// Provenance: repo:tds-reconciliation-portal:frontend/src/pages/ReviewQueuePage.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; runs are
// shown by their 8-hex tag; the maker≠checker button honesty and the batch door carry.
// NEW here: the resolution verbs go through step-up (guide §7.2, lib/stepup) — the platform
// gate the predecessor's session-auth world never had.
//
// Checker side of the maker-checker workflow: pending resolutions to
// approve/reject, plus reversal requests and orphaned resolutions needing
// re-review after a re-run.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { Link, useParams } from 'react-router'

import { ApiError } from '../../api/client'
import { usePeriodSummary } from '../../api/hooks'
import { dupBatches, useRawDupBatchReview, useRawDupDecision, useRawDupReview } from '../../api/rawDupReview'
import {
  useApproveMutation,
  useOrphanedResolutions,
  useRejectMutation,
  useReviewQueue,
} from '../../api/resolutions'
import type { RawDupPending, Resolution } from '../../api/types'
import { Chip } from '../../components/badges'
import { InfoTip } from '../../components/InfoTip'
import { Btn, Modal } from '../../components/Modal'
import { Money } from '../../components/Money'
import { TablePager } from '../../components/TablePager'
import { showToast } from '../../components/toast'
import { runTag } from '../../format'
import { useUserId } from '../../lib/identity'
import { stepUpPost } from '../../lib/stepup'

const th = 'px-2 py-1.5 text-left text-xs font-medium uppercase text-muted-foreground'
const td = 'px-2 py-1.5'

function detailText(r: Resolution): string {
  const parts = [r.comment ?? undefined]
  for (const [k, v] of Object.entries(r.details ?? {})) parts.push(`${k}: ${v}`)
  return parts.filter(Boolean).join(' · ')
}

/** Row-grain duplicate verdicts awaiting a checker.
 *
 *  `duplicate_verdict` is in the DEFAULT maker-checker set, so on an ordinary
 *  client every tick on Raw Data lands here. Rejecting puts the work back —
 *  the row reads as unjudged, the outstanding count goes up again, and the
 *  preparer can judge it afresh. That is the difference between a review and
 *  a rubber stamp.
 */
function RawDuplicateReview({ clientId }: { clientId: string }) {
  const userId = useUserId()
  const q = useRawDupReview(clientId)
  const decide = useRawDupDecision()
  const batchReview = useRawDupBatchReview()
  const [rejecting, setRejecting] = useState<RawDupPending | null>(null)
  const [why, setWhy] = useState('')

  const approveBatch = (uploadId: string, filename: string) =>
    batchReview
      .mutateAsync({ uploadId, decision: 'approve' })
      .then((r) => {
        showToast({
          color: 'green',
          title: `${r.reviewed} verdict(s) approved on ${filename}`,
          message:
            `${r.entries_settled} entr${r.entries_settled === 1 ? 'y' : 'ies'} settled.`
            + (r.skipped_own
              ? ` ${r.skipped_own} you recorded yourself were skipped — a verdict`
                + ' cannot be approved by the person who recorded it.'
              : ''),
        })
      })
      .catch((e) =>
        showToast({
          color: 'red',
          title: 'Batch approval failed',
          message: e instanceof ApiError ? e.message : String(e),
        }),
      )

  const run = (id: string, decision: 'approve' | 'reject', comment?: string) =>
    decide
      .mutateAsync({ id, decision, comment })
      .then(() => {
        showToast({
          color: decision === 'approve' ? 'green' : 'yellow',
          title:
            decision === 'approve'
              ? 'Verdict approved — the row counts as reviewed'
              : 'Verdict rejected — the row is outstanding again',
        })
        setRejecting(null)
        setWhy('')
      })
      .catch((e) =>
        showToast({
          color: 'red',
          title: 'Action failed',
          message: e instanceof ApiError ? e.message : String(e),
        }),
      )

  if (!q.data?.length) return null
  // One decision on Raw Data can record hundreds of row verdicts. The rows
  // below stay for reading and for the odd rejection; the batch button is how
  // a checker who has read the batch passes it without clicking Approve 233
  // times.
  const all = dupBatches(q.data, userId ?? undefined)
  const batches = all.filter((b) => b.theirs > 1)
  // Seen live in the predecessor: the admin recorded keep-one, came here, and
  // found 233 disabled buttons and no button — nothing on screen said WHY.
  // The rule is right (the recorder cannot be the reviewer); the silence
  // about it was not.
  const stuck = all.filter((b) => b.theirs === 0)
  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-2">
        <h2 className="text-sm font-semibold text-foreground">Duplicate verdicts awaiting review</h2>
        <p className="text-xs text-muted-foreground">
          {q.data.length} row(s) — recorded on Raw Data, not yet counted as reviewed
        </p>
      </div>
      {stuck.map((b) => (
        <p key={b.upload_id} className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          All <b>{b.total}</b> pending verdict(s) on <b>{b.filename}</b> were
          recorded by you, and a verdict cannot be approved by the person who
          recorded it. A <b>different</b> admin or reviewer has to sign in and
          approve them — if none exists yet, add one on the org&apos;s console.
        </p>
      ))}
      {batches.map((b) => (
        <div key={b.upload_id} className="flex flex-wrap items-center gap-2">
          <Btn size="xs" kind="primary" loading={batchReview.isPending}
            onClick={() => void approveBatch(b.upload_id, b.filename)}>
            Approve all {b.theirs} on {b.filename}
          </Btn>
          {b.theirs < b.total && (
            <p className="text-xs text-muted-foreground">
              {b.total - b.theirs} of the {b.total} are your own and will stay pending
              — a verdict needs a second pair of eyes.
            </p>
          )}
        </div>
      ))}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[820px] text-xs">
          <thead>
            <tr>
              <th className={th}>Row</th>
              <th className={th}>Party</th>
              <th className={th}>Date</th>
              <th className={`${th} text-right`}>Amount</th>
              <th className={th}>Verdict</th>
              <th className={th}>Preparer&apos;s note</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {q.data.map((v) => {
              // the maker cannot pass their own work; the server refuses it
              // too, this only stops the button pretending otherwise
              const own = v.resolved_by === userId
              return (
                <tr key={v.id} className="border-t border-border">
                  <td className={td}>
                    <p className="text-sm font-semibold text-foreground">#{v.row_index}</p>
                    <p className="truncate text-xs text-muted-foreground">{v.filename}</p>
                  </td>
                  <td className={td} style={{ maxWidth: 240 }}>
                    <p className="text-sm text-foreground">{v.party ?? '—'}</p>
                    {v.section && <p className="text-xs text-muted-foreground">{v.section}</p>}
                  </td>
                  <td className={td}>{v.date ?? '—'}</td>
                  <td className={`${td} text-right`}>
                    {v.amount != null ? <Money v={v.amount} /> : '—'}
                  </td>
                  <td className={td}>
                    <Chip tone={v.verdict === 'genuine' ? 'green' : 'gray'}>
                      {v.verdict === 'genuine' ? 'genuine duplicate' : 'not a duplicate'}
                    </Chip>
                  </td>
                  <td className={td} style={{ maxWidth: 220 }}>
                    <p className="text-xs text-muted-foreground">{v.comment || '—'}</p>
                  </td>
                  <td className={td}>
                    <span className="flex flex-nowrap gap-1.5">
                      <Btn size="xs" kind="primary" disabled={own}
                        title={own ? 'You recorded this verdict' : undefined}
                        loading={decide.isPending}
                        onClick={() => void run(v.id, 'approve')}>
                        Approve
                      </Btn>
                      <Btn size="xs" kind="danger" disabled={own}
                        onClick={() => setRejecting(v)}>
                        Reject
                      </Btn>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <Modal
        opened={rejecting != null}
        onClose={() => setRejecting(null)}
        title={`Reject the verdict on row #${rejecting?.row_index ?? ''}`}
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            The row goes back to outstanding and can be judged again. Nothing is deleted.
          </p>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-foreground">Why</span>
            <textarea
              className="min-h-20 w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
              placeholder="e.g. these are two separate vouchers with the same amount"
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              required
            />
          </label>
          <div className="flex justify-end gap-2">
            <Btn onClick={() => setRejecting(null)}>Cancel</Btn>
            <Btn kind="danger" disabled={why.trim().length < 3} loading={decide.isPending}
              onClick={() => {
                const t = rejecting
                if (t) void run(t.id, 'reject', why.trim())
              }}>
              Reject
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export function ReviewQueuePage() {
  // the hub lives INSIDE the workspace (/w/:clientId/:periodId/approvals)
  // so the workspace tabs never disable
  const params = useParams()
  const summaryQ = usePeriodSummary(params.periodId ?? '')
  const clientId = params.clientId ?? summaryQ.data?.client.id ?? ''
  const base = params.clientId && params.periodId
    ? `/w/${params.clientId}/${params.periodId}` : null
  const userId = useUserId()
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(100)
  const queue = useReviewQueue(clientId, page, size)
  const orphans = useOrphanedResolutions(clientId)
  const approveMut = useApproveMutation()
  const rejectMut = useRejectMutation()
  const [rejecting, setRejecting] = useState<Resolution | null>(null)
  const [rejectComment, setRejectComment] = useState('')
  const [peek, setPeek] = useState<string | null>(null)
  // approving a requested undo — step-up gated like every review verb
  const approveReversal = useMutation({
    mutationFn: (id: string) =>
      stepUpPost(`/resolutions/${id}/reversal/approve`, { comment: 'approved from the hub' }),
    onSuccess: () => {
      showToast({ color: 'green', title: 'Undo approved — the entries re-open.' })
      for (const k of ['review-queue', 'cleared', 'difference', 'difference-parties',
        'reconciliation', 'resolutions', 'party']) {
        void qc.invalidateQueries({ queryKey: [k] })
      }
    },
    onError: (e) => showToast({
      color: 'red', title: 'Could not approve the undo',
      message: e instanceof ApiError ? e.message : String(e),
    }),
  })
  const partyLink = (r: Resolution) =>
    base && r.party_id != null
      ? `${base}/reconciliation/parties/${r.party_id}?run=${r.last_seen_run_id ?? ''}` : null

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn()
      showToast({ color: 'green', title: ok })
    } catch (e) {
      showToast({
        color: 'red',
        title: 'Action failed',
        message: e instanceof ApiError ? e.message : String(e),
      })
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-2">
        <h1 className="text-base font-semibold text-foreground">Approvals</h1>
        {queue.data && queue.data.total > 0 && (
          <p className="text-xs text-muted-foreground">
            {queue.data.total} resolution(s) pending
          </p>
        )}
      </div>

      <RawDuplicateReview clientId={clientId} />

      {queue.isPending && (
        <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
      )}
      {queue.isError && (
        <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          Could not load queue: {String(queue.error)}
        </p>
      )}

      {queue.data && queue.data.total === 0 && (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
          Nothing pending review.
        </p>
      )}

      {queue.data && queue.data.total > 0 && (
        <>
          {queue.data.total > size && (
            <TablePager total={queue.data.total} page={page} size={size}
              onPage={setPage} onSize={setSize} />
          )}
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[860px] text-xs">
              <thead>
                <tr>
                  <th className={th}>Item</th>
                  <th className={th}>Reason</th>
                  <th className={th}>Explanation / details</th>
                  <th className={th}>Evidence</th>
                  <th className={th}>Run</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody>
                {queue.data.items.map((r) => {
                  const own = r.resolved_by === userId
                  const link = partyLink(r)
                  return (
                    <Fragment key={r.id}>
                      <tr className="cursor-pointer border-t border-border"
                        onClick={() => setPeek(peek === r.id ? null : r.id)}>
                        <td className={td} style={{ maxWidth: 260 }}>
                          {/* the queue never dead-ends — the party name IS
                              the door to its context */}
                          {link ? (
                            <Link to={link} className="text-sm font-semibold text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}>
                              {r.item ?? `${r.fingerprint.slice(0, 12)}…`}
                            </Link>
                          ) : (
                            <p className="text-sm font-semibold text-foreground">
                              {r.item ?? `${r.fingerprint.slice(0, 12)}…`}
                            </p>
                          )}
                          <span className="flex flex-nowrap items-center gap-1.5">
                            <Chip tone="gray" className="border border-border bg-transparent">
                              {r.item_kind}
                            </Chip>
                            {r.context && (
                              <span className="truncate text-xs text-muted-foreground">{r.context}</span>
                            )}
                          </span>
                          {r.amount != null && <Money v={r.amount} className="text-xs" />}
                        </td>
                        <td className={td}>
                          <p className="text-sm font-semibold text-foreground">{r.reason_code}</p>
                        </td>
                        <td className={td} style={{ maxWidth: 320 }}>
                          <p className="text-sm text-foreground">{detailText(r) || '—'}</p>
                        </td>
                        <td className={td}>
                          {r.evidence_upload_id != null ? (
                            <span className="text-sm text-primary">evidence attached</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className={td}>
                          <span className="font-mono text-sm text-primary">
                            {r.last_seen_run_id != null ? runTag(r.last_seen_run_id) : '?'}
                          </span>
                        </td>
                        <td className={td}>
                          <span className="flex flex-nowrap justify-end gap-1.5">
                            <Btn size="xs" kind="primary" disabled={own}
                              title={own ? 'Reviewer must differ from the resolver' : undefined}
                              loading={approveMut.isPending}
                              onClick={() =>
                                void act(() => approveMut.mutateAsync(r.id), 'Approved')
                              }>
                              Approve
                            </Btn>
                            <Btn size="xs" kind="danger" disabled={own}
                              onClick={() => {
                                setRejecting(r)
                                setRejectComment('')
                              }}>
                              Reject
                            </Btn>
                          </span>
                        </td>
                      </tr>
                      {peek === r.id && (
                        <tr>
                          <td colSpan={6} className={`${td} bg-muted`}>
                            <p className="text-xs text-muted-foreground">
                              {detailText(r) || 'no comment or details recorded'}
                              {' · '}kind {r.item_kind}
                              {r.details?.member_fingerprints
                                ? ` · ${(r.details.member_fingerprints as unknown as string[]).length} entries in the group`
                                : ''}
                              {link ? ' · click the name for the full party' : ''}
                            </p>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
          {queue.data.total > size && (
            <TablePager total={queue.data.total} page={page} size={size}
              onPage={setPage} onSize={setSize} compact />
          )}
        </>
      )}

      {(queue.data?.reversals?.length ?? 0) > 0 && (
        <>
          <div className="flex items-baseline gap-2">
            <h2 className="text-sm font-semibold text-foreground">Undo requests</h2>
            <p className="text-xs text-muted-foreground">
              {queue.data!.reversals.length} approved disposition(s) someone
              asked to reverse
            </p>
            <InfoTip>
              A maker asked to undo an approved match or explanation. Until a
              checker agrees, the disposition stays in force and keeps its
              claims — approving here re-opens the entries.
            </InfoTip>
          </div>
          <div className="space-y-1.5 rounded-lg border border-border p-2.5">
            {queue.data!.reversals.map((r) => {
              const ownReq = r.reversal_requested_by === userId
              const link = partyLink(r)
              return (
                <div key={r.id} className="flex flex-nowrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    {link ? (
                      <Link to={link} className="text-sm font-semibold text-primary hover:underline">
                        {r.item ?? `${r.fingerprint.slice(0, 16)}…`}
                      </Link>
                    ) : (
                      <p className="truncate text-sm font-semibold text-foreground">
                        {r.item ?? `${r.fingerprint.slice(0, 16)}…`}
                      </p>
                    )}
                    <p className="truncate text-xs text-muted-foreground">
                      {r.reason_code}
                      {r.reversal_comment ? ` · ${r.reversal_comment}` : ''}
                    </p>
                  </div>
                  <span className="flex flex-nowrap items-center gap-3">
                    {r.amount != null && <Money v={r.amount} />}
                    <Btn size="xs" kind="warning" disabled={ownReq}
                      title={ownReq
                        ? 'The reviewer must differ from the requester'
                        : undefined}
                      loading={approveReversal.isPending}
                      onClick={() => approveReversal.mutate(r.id)}>
                      Approve undo
                    </Btn>
                  </span>
                </div>
              )
            })}
          </div>
        </>
      )}

      {(orphans.data?.length ?? 0) > 0 && (
        <>
          <div className="flex items-baseline gap-2">
            <h2 className="text-sm font-semibold text-foreground">Orphaned resolutions</h2>
            <p className="text-xs text-muted-foreground">
              {orphans.data?.length} carried on items that no longer exist in the latest run
            </p>
            <InfoTip>
              The source data changed and the item these resolutions were made on has
              vanished. Re-review: resolve the replacement items and let these lapse,
              or investigate why the item disappeared.
            </InfoTip>
          </div>
          <div className="space-y-1.5 rounded-lg border border-border p-2.5">
            {orphans.data?.map((r) => (
              <div key={r.id} className="flex flex-nowrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {r.item ?? `${r.fingerprint.slice(0, 16)}…`}
                  </p>
                  {r.context && (
                    <p className="truncate text-xs text-muted-foreground">{r.context}</p>
                  )}
                </div>
                <span className="flex flex-nowrap items-center gap-3">
                  {r.amount != null && <Money v={r.amount} />}
                  <span className="text-sm text-foreground">{r.reason_code}</span>
                  <Chip tone="red">{r.status}</Chip>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal
        opened={rejecting !== null}
        onClose={() => setRejecting(null)}
        title="Reject resolution"
      >
        <div className="space-y-3">
          <p className="text-sm text-foreground">
            Rejecting <b>{rejecting?.reason_code}</b> returns the item to the preparer. A comment
            is required.
          </p>
          <textarea
            className="min-h-16 w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder="Why is this resolution not acceptable?"
            required
          />
          <div className="flex justify-end gap-2">
            <Btn onClick={() => setRejecting(null)}>Cancel</Btn>
            <Btn kind="danger" disabled={rejectComment.trim().length < 3}
              loading={rejectMut.isPending}
              onClick={() => {
                const target = rejecting
                if (!target) return
                void act(
                  () =>
                    rejectMut
                      .mutateAsync({ resolutionId: target.id, comment: rejectComment.trim() })
                      .then(() => setRejecting(null)),
                  'Rejected - returned to the preparer',
                )
              }}>
              Reject
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  )
}
